/**
 * **The stickers made of the climb's words**, white with a shadow, laid over
 * the media and moved and sized like the card:
 *
 * - the **name**: the climb's type glyph and what it is called on the wall —
 *   its grade, its holds' colour and its zone (`climbHeading`) — with the name
 *   its setter gave it, if any, under it;
 * - the **stats**: its grade, how it was sent and its rating, stacked over a
 *   hatched band, Garmin style — what used to be the Info layout's left corner.
 *
 * Everything is measured in the sticker's main font size, so each is the same
 * shape at every size; the width follows from the words. `…Aspect` gives that
 * shape (height over width) for the placement rules, which keep a sticker's
 * width only (see `thumbnail-placement.ts`).
 */

export interface NameStickerInfo {
  heading: string;
  /** The name its setter gave it, when it is not the heading already. */
  subtitle: string | null;
  climbType: 'Boulder' | 'Route';
}

export interface StatsStickerInfo {
  grade: string | null;
  sendValue: string;
  ratingAvg: number | null;
  labels: { grade: string; send: string; rating: string };
}

const FONT_FAMILY = 'system-ui, -apple-system, "Segoe UI", sans-serif';
/** Longer lines are cut short with an ellipsis. */
const MAX_CHARS = 40;

function short(text: string): string {
  return text.length > MAX_CHARS ? `${text.slice(0, MAX_CHARS - 1)}…` : text;
}

const widths = new Map<string, number>();

/** How wide `text` is written in `weight` at a font size of one pixel (measured once: a video asks every frame). */
function textWidth(text: string, weight: number): number {
  const key = `${weight}|${text}`;
  const known = widths.get(key);
  if (known != null) return known;
  const ctx = typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;
  let width = text.length * 0.56;
  if (ctx) {
    ctx.font = `${weight} 100px ${FONT_FAMILY}`;
    width = ctx.measureText(text).width / 100;
  }
  widths.set(key, width);
  return width;
}

function shadowed(ctx: CanvasRenderingContext2D, blur: number) {
  ctx.shadowColor = 'rgba(0,0,0,0.85)';
  ctx.shadowBlur = blur;
}

// ── The name ─────────────────────────────────────────────────────────────────
// In shares of the heading's font size.
const GLYPH = 0.9;
const GLYPH_GAP = 0.4;
const SUBTITLE = 0.55;
const LINE = 1.3;
const NAME_PAD = 0.15;

function nameUnits(info: NameStickerInfo): { width: number; height: number } {
  const heading = textWidth(short(info.heading), 700);
  const subtitle = info.subtitle ? textWidth(short(info.subtitle), 500) * SUBTITLE : 0;
  return {
    width: GLYPH + GLYPH_GAP + Math.max(heading, subtitle) + NAME_PAD,
    height: LINE + (info.subtitle ? SUBTITLE * LINE : 0),
  };
}

export function nameStickerAspect(info: NameStickerInfo): number {
  const { width, height } = nameUnits(info);
  return height / width;
}

/** The climb-type glyph: a mountain, filled for a boulder, outlined for a route. */
function drawGlyph(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, type: 'Boulder' | 'Route') {
  ctx.save();
  shadowed(ctx, r * 0.4);
  ctx.beginPath();
  ctx.moveTo(cx - r, cy + r * 0.8);
  ctx.lineTo(cx, cy - r);
  ctx.lineTo(cx + r, cy + r * 0.8);
  ctx.closePath();
  if (type === 'Boulder') {
    ctx.fillStyle = '#ffffff';
    ctx.fill();
  } else {
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = Math.max(2, r * 0.18);
    ctx.lineJoin = 'round';
    ctx.stroke();
  }
  ctx.restore();
}

export function drawNameSticker(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, info: NameStickerInfo) {
  const font = width / nameUnits(info).width;
  drawGlyph(ctx, x + (GLYPH / 2) * font, y + (LINE / 2) * font, (GLYPH / 2) * font * 0.9, info.climbType);
  const textX = x + (GLYPH + GLYPH_GAP) * font;
  ctx.save();
  shadowed(ctx, font * 0.2);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#ffffff';
  ctx.font = `700 ${font}px ${FONT_FAMILY}`;
  ctx.fillText(short(info.heading), textX, y + (LINE / 2) * font);
  if (info.subtitle) {
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.font = `500 ${SUBTITLE * font}px ${FONT_FAMILY}`;
    ctx.fillText(short(info.subtitle), textX, y + (LINE + (SUBTITLE * LINE) / 2) * font);
  }
  ctx.restore();
}

// ── The stats ────────────────────────────────────────────────────────────────
// In shares of the values' font size.
const HATCH = 0.6;
const STATS_TEXT_X = 0.95;
const LABEL = 0.33;
const BLOCK = 2.0;
const STATS_PAD = 0.2;
const STAR = 0.7;

function statBlocks(info: StatsStickerInfo) {
  return [
    { value: info.grade || '—', label: info.labels.grade, star: false },
    { value: info.sendValue, label: info.labels.send, star: false },
    { value: info.ratingAvg != null ? info.ratingAvg.toFixed(1) : '—', label: info.labels.rating, star: info.ratingAvg != null },
  ].map((b) => ({ ...b, value: short(b.value) }));
}

function statsUnits(info: StatsStickerInfo): { width: number; height: number } {
  const widest = Math.max(...statBlocks(info).map((b) =>
    Math.max(
      textWidth(b.value, 700) + (b.star ? 0.12 + textWidth('★', 400) * STAR : 0),
      textWidth(b.label.toUpperCase(), 600) * LABEL,
    )));
  return { width: STATS_TEXT_X + widest + STATS_PAD, height: BLOCK * 3 - 0.4 };
}

export function statsStickerAspect(info: StatsStickerInfo): number {
  const { width, height } = statsUnits(info);
  return height / width;
}

export function drawStatsSticker(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, info: StatsStickerInfo) {
  const units = statsUnits(info);
  const font = width / units.width;
  const height = units.height * font;

  // The hatched band down the left.
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, HATCH * font, height);
  ctx.clip();
  ctx.strokeStyle = 'rgba(255,255,255,0.55)';
  ctx.lineWidth = Math.max(1.5, font * 0.075);
  const step = font * 0.4;
  for (let yy = y; yy <= y + height + HATCH * font; yy += step) {
    ctx.beginPath();
    ctx.moveTo(x, yy);
    ctx.lineTo(x + HATCH * font, yy - HATCH * font);
    ctx.stroke();
  }
  ctx.restore();

  const textX = x + STATS_TEXT_X * font;
  statBlocks(info).forEach((b, i) => {
    const baseline = y + (i * BLOCK + 0.85) * font;
    ctx.save();
    shadowed(ctx, font * 0.1);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.font = `700 ${font}px ${FONT_FAMILY}`;
    ctx.fillStyle = '#ffffff';
    ctx.fillText(b.value, textX, baseline);
    if (b.star) {
      const valueWidth = ctx.measureText(b.value).width;
      ctx.font = `400 ${font * STAR}px ${FONT_FAMILY}`;
      ctx.fillStyle = '#FACC15';
      ctx.fillText('★', textX + valueWidth + font * 0.12, baseline);
    }
    ctx.font = `600 ${font * LABEL}px ${FONT_FAMILY}`;
    ctx.fillStyle = 'rgba(255,255,255,0.62)';
    ctx.fillText(b.label.toUpperCase(), textX, baseline + font * LABEL * 1.5);
    ctx.restore();
  });
}
